import React, { useEffect } from 'react';
import { EbillsImportSettingsCards, useBillProviders } from '@melio/ap-activities';
import { Group, Typography } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { getAvailableToConnectAccountingPlatforms, useAccountingPlatforms } from '@melio/platform-api';
import { AccountingPlatformSlug } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useLocation } from '@melio/platform-utils';

import { WithLoading } from '@/hoc/withLoading.hoc';
import { AccountingPlatformStateType } from '@/hooks/router.hooks';
import { usePlatformIntl } from '@/translations/Intl';
import { getAccessTokenOrganizationId } from '@/utils/getAccessTokenData.utils';
import { AccountingPlatformCardQbo } from '@/widgets/accounting-platforms/AccountingPlatformCards/AccountingPlatformCardQbo.widget';
import { AccountingPlatformCardXero } from '@/widgets/accounting-platforms/AccountingPlatformCards/AccountingPlatformCardXero.widget';

export const AccountingSoftwareContainer = () => {
  const { data, isLoading } = useAccountingPlatforms();
  const { track } = useAnalytics();
  const { formatMessage } = usePlatformIntl();
  const { state } = useLocation<AccountingPlatformStateType>();
  const [platformXeroAccountingSoftwareEnabled] = useFeature<boolean>(
    FeatureFlags.PlatformXeroAccountingSoftwareEnabled,
    false,
  );

  const providers = useBillProviders();

  useEffect(() => {
    track('Settings', 'View', {
      PageName: 'sync-and-import',
      Intent: 'sync',
      Flow: 'settings',
      PageEntryPoint: 'settings',
    });
  }, [track]);

  const renderAccountingPlatformList = React.useCallback(() => {
    const organizationId = getAccessTokenOrganizationId();
    const { syncError, accountingPlatformId } = state || {};
    if (syncError != undefined) {
      window.history.replaceState({}, document.title);
    }
    if (data && organizationId) {
      const accountingPlatforms = getAvailableToConnectAccountingPlatforms(data);
      return accountingPlatforms.map((accountingPlatform, index) => {
        const { accountingSlug } = accountingPlatform;
        const shouldShowSyncError = accountingPlatformId === accountingPlatform.id;
        const error = shouldShowSyncError ? syncError : undefined;

        switch (accountingSlug) {
          case AccountingPlatformSlug.QuickBooksOnline:
            return (
              <AccountingPlatformCardQbo
                key={`${index}_${accountingPlatform.id}`}
                accountingPlatform={accountingPlatform}
                organizationId={organizationId}
                syncError={error}
              />
            );
          case AccountingPlatformSlug.Xero:
            return (
              platformXeroAccountingSoftwareEnabled && (
                <AccountingPlatformCardXero
                  key={`${index}_${accountingPlatform.id}`}
                  accountingPlatform={accountingPlatform}
                  organizationId={organizationId}
                  syncError={error}
                />
              )
            );
          default:
            return null;
        }
      });
    }
    return null;
  }, [data, state, platformXeroAccountingSoftwareEnabled]);

  return (
    <WithLoading isLoading={isLoading}>
      <Group
        alignItems="stretch"
        height="auto"
        justifyContent="stretch"
        spacing="xl"
        textAlign="start"
        variant="vertical"
        width="auto"
      >
        {Boolean(providers.length) && (
          <Typography.SectionLabel label={formatMessage('widgets.accountingPlatform.header')} />
        )}
        {renderAccountingPlatformList()}
        <EbillsImportSettingsCards />
      </Group>
    </WithLoading>
  );
};
